import type { KeyboardEventHandler, Ref } from 'react';
import React, { useId } from 'react';
import { HBox, VBox } from '../../layout';
import { Badge, Icon, IconToolTip } from '../../basic';
import { intl, intlFmt } from '../../../lib';
import type { ChatUser, MessageStatus } from '../chatTypes';
import { ChatPersonImage } from './ChatPersonImage';
import { formatChatMessageDate } from './chatDateFormatting';
import { withScaledEmoji } from './scaledEmoji';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

/**
 * Props for a {@link ChatTextMessage} component.
 */
export type ChatMessageProps = {
  /**
   * Person sending the message.
   */
  person: Omit<ChatUser, 'uid'>;
  /**
   * Date when message was sent. You can specify string for your date formatting, otherwise default will be used which renders time for today and date for older messages.
   */
  date: Date | string;
  /**
   * Text of the message.
   */
  text: string;
  /**
   * Text of the error.
   */
  errorText?: string;
  /**
   * Status of the message.
   */
  status?: MessageStatus;
  /**
   * Tab order for keyboard navigation within the chat message list.
   */
  tabIndex?: number;
  /**
   * Ref to the focusable message container.
   */
  messageRef?: Ref<HTMLDivElement>;
  /**
   * Keyboard handler for navigating between messages.
   */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /**
   * Focus handler to keep roving tabindex state in sync.
   */
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  emojiData?: EmojiData;
};

export default function ChatTextMessage({
  person,
  date,
  text,
  errorText,
  status,
  tabIndex,
  messageRef,
  onKeyDown,
  emojiData = { categories: {}, emojis: {} },
  onFocus
}: Readonly<ChatMessageProps>): React.JSX.Element {
  const { firstname, lastname, isSender } = person;
  const displayName = person.displayName ?? `${firstname} ${lastname}`.trim();
  const messageFromLabel = intlFmt(
    'common.messageFrom',
    displayName,
    ''
  ).trim();
  const formattedDate = formatChatMessageDate(date);
  const userStatusId = useId();
  const errorId = useId();

  const ariaDescribedBy = [];
  if (person.status && !isSender) {
    ariaDescribedBy.push(userStatusId);
  }
  if (status === 'error') {
    ariaDescribedBy.push(errorId);
  }

  return (
    <HBox
      ref={messageRef}
      className={`ehs-chat-message chat-text-component ${
        isSender ? 'sender' : ''
      }`}
      align={isSender ? 'end' : 'start'}
      aria-live="polite"
      role="group"
      tabIndex={tabIndex ?? -1}
      aria-describedby={ariaDescribedBy.join(' ') || undefined}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
    >
      <div className="sr-only">
        {!isSender ? messageFromLabel : intl('common.you')}
      </div>
      {!isSender && (
        <ChatPersonImage
          avatar="user"
          firstname={firstname}
          lastname={lastname}
          userStatus={person.status}
          aria-hidden="true"
          userStatusId={userStatusId}
        />
      )}
      <VBox className="text-message">
        <HBox className="text-message-header" wrap="wrap">
          {!isSender && (
            <>
              <div aria-hidden="true" className="username">
                {displayName}
              </div>
              <Icon
                iconName="dot"
                aria-label={undefined}
                className="text-message-dot-icon gray"
              />
            </>
          )}
          {isSender && status === 'read' && (
            <IconToolTip
              tooltip={intl('common.chatmessage.read')}
              iconName="eye-modern"
              className="message-status"
            />
          )}
          {isSender && status === 'sent' && (
            <IconToolTip
              tooltip={intl('common.chatmessage.sent')}
              iconName="shui_check"
              className="message-status"
            />
          )}
          {isSender && status === 'sending' && (
            <IconToolTip
              tooltip={intl('common.chatmessage.sending')}
              iconName="more-options"
              className="message-status"
            />
          )}
          <div className="gray">
            <time
              dateTime={formattedDate}
              aria-label={intlFmt('common.chatmessage.sentOn', formattedDate)}
            >
              {formattedDate}
            </time>
          </div>
        </HBox>
        <div className={`text-message-body ${isSender ? 'sender' : ''}`}>
          <p className="chat-message-text">
            {withScaledEmoji(text, emojiData)}
          </p>
        </div>
        {status === 'error' && (
          <HBox className="chat-message-error" role="alert" id={errorId}>
            <Badge badgeType="error" />
            <p>
              {errorText ? errorText : intl('common.chatmessage.defaultError')}
            </p>
          </HBox>
        )}
      </VBox>
    </HBox>
  );
}
